import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { Undoable } from '@medsurf/flat-decorators';
import { HistoryEntityBaseState } from '../base/history-entity.base.state';

export const ANNOTATION_LABEL_ENTITY_TOKEN = new StateToken<AnnotationLabelEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.ANNOTATION_LABEL_ENTITY);

export type AnnotationLabelEntityStateModel = FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.AnnotationLabelEntityModels.AnnotationLabel>;

@State<AnnotationLabelEntityStateModel>({
  name: ANNOTATION_LABEL_ENTITY_TOKEN,
  defaults: {
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class AnnotationLabelEntityState extends HistoryEntityBaseState<FlatModels.AnnotationLabelEntityModels.AnnotationLabel, AnnotationLabelEntityStateModel> {
  //<editor-fold desc="Selectors">

  @Selector([AnnotationLabelEntityState])
  static override typedEntities$(state: AnnotationLabelEntityStateModel): { [id: string]: FlatModels.AnnotationLabelEntityModels.AnnotationLabel } {
    return HistoryEntityBaseState.typedEntities$(state);
  }

  @Selector([AnnotationLabelEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.AnnotationLabelEntityModels.AnnotationLabel }): FlatModels.AnnotationLabelEntityModels.AnnotationLabel[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }

  @Selector([AnnotationLabelEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.AnnotationLabelEntityModels.AnnotationLabel }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }

  @Selector([AnnotationLabelEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.AnnotationLabelEntityModels.AnnotationLabel }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.AnnotationLabelEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<AnnotationLabelEntityStateModel>,
                            action: FlatActions.AnnotationLabelEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }

  @Action(FlatActions.AnnotationLabelEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<AnnotationLabelEntityStateModel>,
                              action: FlatActions.AnnotationLabelEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }

  @Action(FlatActions.AnnotationLabelEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<AnnotationLabelEntityStateModel>,
                               action: FlatActions.AnnotationLabelEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }

  @Action(FlatActions.AnnotationLabelEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<AnnotationLabelEntityStateModel>,
                                 action: FlatActions.AnnotationLabelEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.AnnotationLabelEntityActions.AddEntity)
  @Undoable(
    FlatActions.AnnotationLabelEntityActions.AddEntity,
    ANNOTATION_LABEL_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<AnnotationLabelEntityStateModel>,
                            action: FlatActions.AnnotationLabelEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }

  @Action(FlatActions.AnnotationLabelEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.AnnotationLabelEntityActions.UpdateEntity,
    ANNOTATION_LABEL_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<AnnotationLabelEntityStateModel>,
                        action: FlatActions.AnnotationLabelEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }

  @Action(FlatActions.AnnotationLabelEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.AnnotationLabelEntityActions.RemoveEntity,
    ANNOTATION_LABEL_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<AnnotationLabelEntityStateModel>,
                        action: FlatActions.AnnotationLabelEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">

  @Action(FlatActions.AnnotationLabelEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<AnnotationLabelEntityStateModel>,
                        action: FlatActions.AnnotationLabelEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }

  @Action(FlatActions.AnnotationLabelEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<AnnotationLabelEntityStateModel>,
                        action: FlatActions.AnnotationLabelEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }

  @Action(FlatActions.AnnotationLabelEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<AnnotationLabelEntityStateModel>,
                            action: FlatActions.AnnotationLabelEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }

  //</editor-fold>

  //</editor-fold>
}
